/**
 * SorryPage - Light Theme Error Page
 * 
 * Eye-friendly design with:
 * - Clean white backgrounds
 * - Soft animations
 * - Muted accent colors
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { keyframes } from '@emotion/react';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(3deg); }
`;

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
`;

// Floating Shape Component - Light Theme
const FloatingShape = ({ size, top, left, right, bottom, delay, filled = false }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      right,
      bottom,
      width: size,
      height: size,
      borderRadius: filled ? '12px' : '50%',
      border: filled ? 'none' : '2px solid rgba(78, 205, 196, 0.3)',
      background: filled ? 'rgba(78, 205, 196, 0.05)' : 'transparent',
      opacity: 0.6,
      animation: `${float} ${6 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      transform: filled ? 'rotate(15deg)' : 'none',
      pointerEvents: 'none',
    }}
  />
);

export default function SorryPage() {
  const [t] = useTranslation('common');

  const handleGoHome = () => {
    const hostUrl = cookies.get('hostUrl');
    if (hostUrl) {
      window.location.href = hostUrl;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 50%, #E2E8F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating Background Shapes */}
      <FloatingShape size={100} top="10%" left="10%" delay={0} />
      <FloatingShape size={60} top="20%" right="15%" delay={1.5} />
      <FloatingShape size={80} bottom="15%" left="8%" delay={2} />
      <FloatingShape size={50} bottom="20%" right="10%" delay={0.5} />
      <FloatingShape size={70} top="50%" left="5%" delay={1} filled />

      {/* Main Card */}
      <Box
        sx={{
          width: '90%',
          maxWidth: 450,
          background: '#FFFFFF',
          borderRadius: 4,
          padding: 5,
          border: '1px solid #E2E8F0',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1,
          animation: `${fadeInUp} 0.8s ease-out forwards`,
          textAlign: 'center',
        }}
      >
        {/* Sad Icon */}
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: `${shake} 2s ease-in-out infinite`,
          }}
        >
          <SentimentDissatisfiedIcon
            sx={{
              fontSize: 50,
              color: '#EF4444',
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: '#1E293B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 2,
          }}
        >
          {t('GLOBAL.sorry') || 'Sorry!'}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            fontSize: 15,
            color: '#64748B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 3,
            lineHeight: 1.7,
          }}
        >
          {t('GLOBAL.sorryMessage') || 'We couldn\'t redirect you to the login page. Please try logging in again from the main system.'}
        </Typography>

        {/* Action Button */}
        <Button
          onClick={handleGoHome}
          startIcon={<HomeIcon />}
          sx={{
            background: 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
            color: '#fff',
            padding: '12px 28px',
            borderRadius: 2,
            fontFamily: 'Cairo, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(78, 205, 196, 0.25)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #3DBCB4 0%, #5B8ABE 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(78, 205, 196, 0.35)',
            },
          }}
        >
          {t('GLOBAL.returnHome') || 'Return to Main System'}
        </Button>
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 20,
          color: '#94A3B8',
          fontSize: 12,
          fontFamily: 'Cairo, sans-serif',
        }}
      >
        WenkGPS. All rights reserved 2026 ©
      </Typography>
    </Box>
  );
}
